import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useOktaSession } from "../okta/OktaSessionContext.jsx";
import Topbar from "../components/Topbar.jsx";
import Badge from "../components/ui/Badge.jsx";
import Button from "../components/ui/Button.jsx";
import { formatOpenSeverityCountLine } from "../utils/severityDisplay.js";

function formatLong(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function statusVariant(st) {
  const s = String(st || "").toLowerCase();
  if (s === "complete" || s === "completed") return "complete";
  if (s === "active") return "active";
  return "info";
}

export default function ProjectReport() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { clearSession } = useOktaSession();
  const [project, setProject] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let c = false;
    (async () => {
      try {
        const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}`, { credentials: "include" });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || "Failed to load project");
        if (!c) setProject(data.project);
      } catch (e) {
        if (!c) setError(e?.message || "Error");
      } finally {
        if (!c) setLoading(false);
      }
    })();
    return () => {
      c = true;
    };
  }, [projectId]);

  useEffect(() => {
    let c = false;
    fetch("/api/me", { credentials: "include" })
      .then((r) => r.json())
      .then((j) => {
        if (!c) setMe(j);
      })
      .catch(() => {});
    return () => {
      c = true;
    };
  }, []);

  const handleDisconnect = () => {
    clearSession();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ background: "var(--bg-base)", color: "var(--text-secondary)" }}>
        Loading…
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4" style={{ background: "var(--bg-base)" }}>
        <p className="text-critical">{error || "Project not found"}</p>
        <Button variant="secondary" onClick={() => navigate("/")}>
          ← Back to projects
        </Button>
      </div>
    );
  }

  const orgName = project.org?.name || "Project";
  const status = project.status || "active";
  const summary = project.conversationSummary?.summary;
  const decisions = project.conversationSummary?.keyDecisions || [];
  const roadmapItems = project.roadmap?.items || [];
  const executionLog = Array.isArray(project.executionLog) ? [...project.executionLog].reverse() : [];
  const snap = project.auditSnapshot;
  const snapSummary = snap?.summary;

  const byCat = new Map();
  for (const it of roadmapItems) {
    const cat = (it?.category && String(it.category)) || "general";
    if (!byCat.has(cat)) byCat.set(cat, []);
    byCat.get(cat).push(it);
  }

  return (
    <div className="flex min-h-screen flex-col" style={{ background: "var(--bg-base)" }}>
      <Topbar project={project} userEmail={me?.user?.email} onDisconnect={handleDisconnect} />
      <main style={{ maxWidth: "800px", margin: "0 auto", padding: "48px 24px", width: "100%" }}>
        <div style={{ marginBottom: "24px" }}>
          <Badge variant={statusVariant(status)}>{String(status).toUpperCase()}</Badge>
          <h1 style={{ fontSize: "26px", fontWeight: 600, marginTop: "12px", color: "var(--text-primary)" }}>
            {orgName} — Implementation Report
          </h1>
          <p className="text-secondary" style={{ marginTop: "8px" }}>
            Started {formatLong(project.createdAt)} · Last updated {formatLong(project.updatedAt)}
          </p>
        </div>

        <section
          style={{
            marginBottom: "20px",
            padding: "20px",
            borderRadius: "6px",
            border: "1px solid var(--border)",
            background: "var(--bg-surface)",
          }}
        >
          <h2 className="text-dim" style={{ fontSize: "11px", letterSpacing: "0.1em", marginBottom: "12px" }}>
            SUMMARY
          </h2>
          <p style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{summary || "No summary recorded yet."}</p>
        </section>

        <section
          style={{
            marginBottom: "20px",
            padding: "20px",
            borderRadius: "6px",
            border: "1px solid var(--border)",
            background: "var(--bg-surface)",
          }}
        >
          <h2 className="text-dim" style={{ fontSize: "11px", letterSpacing: "0.1em", marginBottom: "12px" }}>
            KEY DECISIONS
          </h2>
          {decisions.length === 0 ? (
            <p className="text-dim">None recorded.</p>
          ) : (
            <ul style={{ listStyle: "disc", paddingLeft: "20px" }}>
              {decisions.map((d, i) => (
                <li key={i} style={{ marginBottom: "6px" }}>
                  {String(d)}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section
          style={{
            marginBottom: "20px",
            padding: "20px",
            borderRadius: "6px",
            border: "1px solid var(--border)",
            background: "var(--bg-surface)",
          }}
        >
          <h2 className="text-dim" style={{ fontSize: "11px", letterSpacing: "0.1em", marginBottom: "12px" }}>
            ROADMAP
          </h2>
          {roadmapItems.length === 0 ? (
            <p className="text-dim">No roadmap items.</p>
          ) : (
            [...byCat.entries()].map(([cat, items]) => (
              <div key={cat} style={{ marginBottom: "16px" }}>
                <p className="text-accent" style={{ fontSize: "12px", fontWeight: 600, marginBottom: "8px" }}>
                  {cat}
                </p>
                <ul style={{ listStyle: "none" }}>
                  {items.map((it) => (
                    <li
                      key={it.id || it.title}
                      style={{
                        padding: "8px 0",
                        borderBottom: "1px solid var(--border)",
                        fontSize: "13px",
                      }}
                    >
                      <span style={{ fontWeight: 500 }}>{it.title}</span>
                      <span className="text-dim" style={{ marginLeft: "8px" }}>
                        {it.status}
                        {it.completedAt ? ` · ${formatLong(it.completedAt)}` : ""}
                      </span>
                      {it.notes ? (
                        <p className="text-secondary" style={{ fontSize: "12px", marginTop: "4px" }}>
                          {it.notes}
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </section>

        <section
          style={{
            marginBottom: "20px",
            padding: "20px",
            borderRadius: "6px",
            border: "1px solid var(--border)",
            background: "var(--bg-surface)",
          }}
        >
          <h2 className="text-dim" style={{ fontSize: "11px", letterSpacing: "0.1em", marginBottom: "12px" }}>
            EXECUTION LOG
          </h2>
          {executionLog.length === 0 ? (
            <p className="text-dim">No entries.</p>
          ) : (
            <ul style={{ listStyle: "none", fontSize: "13px" }}>
              {executionLog.map((e, i) => (
                <li
                  key={i}
                  style={{
                    padding: "10px 0",
                    borderBottom: "1px solid var(--border)",
                  }}
                >
                  <span className="text-dim">{formatLong(e.timestamp || e.at)}</span>
                  {" · "}
                  <span style={{ fontWeight: 500 }}>{e.action || "—"}</span>
                  {e.description ? (
                    <p className="text-secondary" style={{ marginTop: "4px" }}>
                      {e.description}
                    </p>
                  ) : null}
                  {e.oktaObjects ? (
                    <pre
                      className="font-mono text-dim"
                      style={{ fontSize: "11px", marginTop: "6px", whiteSpace: "pre-wrap" }}
                    >
                      {JSON.stringify(e.oktaObjects, null, 2)}
                    </pre>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section
          style={{
            marginBottom: "24px",
            padding: "20px",
            borderRadius: "6px",
            border: "1px solid var(--border)",
            background: "var(--bg-surface)",
          }}
        >
          <h2 className="text-dim" style={{ fontSize: "11px", letterSpacing: "0.1em", marginBottom: "12px" }}>
            AUDIT SNAPSHOT
          </h2>
          {!snap ? (
            <p className="text-dim">No snapshot stored.</p>
          ) : (
            <>
              <p className="text-secondary" style={{ marginBottom: "8px" }}>
                {formatOpenSeverityCountLine(snapSummary, { healthy: snapSummary?.healthyCount ?? 0 })}
              </p>
              <p className="text-dim" style={{ fontSize: "12px" }}>
                Taken on {formatLong(snap.takenAt)}
              </p>
            </>
          )}
        </section>

        <Button variant="secondary" onClick={() => navigate("/")}>
          ← Back to Projects
        </Button>
      </main>
    </div>
  );
}
